import Base from '../base/base';
import './page-loader.scss';

interface PageLoaderOptions {
  debug?: boolean;
  enabled?: boolean;
  oncePerPageSession?: boolean;
  storageKeyPrefix?: string;
  pageKey?: string;
  minimumDurationMs?: number;
  maxDurationMs?: number;
  elementTimeoutMs?: number;
  progressSmoothing?: number;
  completionDisplayThreshold?: number | null;
  trackSelector?: string;
}

interface TrackedMedia {
  id: string;
  element: HTMLImageElement | HTMLVideoElement;
  cleanup: () => void;
}

class PageLoader extends Base {
  private enabled: boolean;

  private oncePerPageSession: boolean;

  private storageKeyPrefix: string;

  private pageKey: string;

  private minimumDurationMs: number;

  private maxDurationMs: number;

  private elementTimeoutMs: number;

  private progressSmoothing: number;

  private completionDisplayThreshold: number | null;

  private trackSelector: string;

  private overlayElement: HTMLDivElement | null = null;

  private startTime = 0;

  private rafId: number | null = null;

  private maxTimer: number | null = null;

  private trackedMedia: TrackedMedia[] = [];

  private itemProgress = new Map<string, number>();

  private targetProgress = 0;

  private displayProgress = 0;

  private completionQueued = false;

  private completionStarted = false;

  constructor(options: PageLoaderOptions = {}) {
    super(options.debug || false);

    this.enabled = options.enabled !== false;
    this.oncePerPageSession = options.oncePerPageSession !== false;
    this.storageKeyPrefix = options.storageKeyPrefix || 'page-loader-seen';
    this.pageKey = options.pageKey || window.location.pathname;
    this.minimumDurationMs = options.minimumDurationMs || 900;
    this.maxDurationMs = options.maxDurationMs || 4500;
    this.elementTimeoutMs = options.elementTimeoutMs || 10000;
    this.progressSmoothing = options.progressSmoothing || 0.18;
    this.completionDisplayThreshold = options.completionDisplayThreshold === undefined
      ? 0.5
      : options.completionDisplayThreshold;
    this.trackSelector = options.trackSelector || 'img, video';
  }

  start(): void {
    if (!this.enabled || !this.shouldShowLoader()) {
      this.log('Page loader skipped');
      return;
    }

    this.startTime = Date.now();
    this.log('Page loader start', {
      startedAt: new Date(this.startTime).toISOString(),
      hardCapMs: this.maxDurationMs,
      minimumDurationMs: this.minimumDurationMs,
      elementTimeoutMs: this.elementTimeoutMs,
      completionDisplayThreshold: this.completionDisplayThreshold,
      pageKey: this.pageKey,
    });

    this.mountOverlay();
    this.startProgressLoop();
    this.beginTrackingMedia();

    this.log('Hard cap timer armed', { hardCapMs: this.maxDurationMs });
    this.maxTimer = window.setTimeout(() => {
      this.log('Max loader timeout reached; forcing completion', {
        targetProgress: this.targetProgress,
        displayProgress: this.displayProgress,
        trackedItems: this.itemProgress.size,
      });
      this.finishTracking('hard-cap');
    }, this.maxDurationMs);
  }

  destroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.maxTimer !== null) {
      window.clearTimeout(this.maxTimer);
      this.maxTimer = null;
    }

    this.trackedMedia.forEach((item) => item.cleanup());
    this.trackedMedia = [];

    if (this.overlayElement && this.overlayElement.parentNode) {
      this.overlayElement.parentNode.removeChild(this.overlayElement);
    }

    this.overlayElement = null;
  }

  private shouldShowLoader(): boolean {
    if (!this.oncePerPageSession) {
      return true;
    }

    try {
      const key = this.getStorageKey();
      return window.sessionStorage.getItem(key) !== '1';
    } catch (error) {
      this.log('Session storage unavailable; loader will show', error);
      return true;
    }
  }

  private markShown(): void {
    if (!this.oncePerPageSession) {
      return;
    }

    try {
      window.sessionStorage.setItem(this.getStorageKey(), '1');
    } catch (error) {
      this.log('Session storage write failed', error);
    }
  }

  private getStorageKey(): string {
    return `${this.storageKeyPrefix}:${this.pageKey}`;
  }

  private mountOverlay(): void {
    if (this.overlayElement) {
      return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'page-loader';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-label', 'Loading page content');
    overlay.innerHTML = this.getOverlayMarkup();

    document.body.appendChild(overlay);
    this.overlayElement = overlay;

    this.applyProgress(0);
  }

  private getOverlayMarkup(): string {
    return `
      <div class="page-loader__word-wrap">
        <svg class="page-loader__word page-loader__word--base" width="283" height="54" viewBox="0 0 283 54" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          ${this.getLogoPaths()}
        </svg>
        <div class="page-loader__word-fill-wrap">
          <svg class="page-loader__word page-loader__word--fill" width="283" height="54" viewBox="0 0 283 54" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            ${this.getLogoPaths()}
          </svg>
        </div>
      </div>
    `;
  }

  private getLogoPaths(): string {
    return `
      <g class="page-loader__rac">
        <path d="M61.03 20.4892C61.22 20.1392 61.1 19.9192 61.03 19.5992C60.18 16.0692 57.45 8.11917 55.98 4.76917C55.81 4.36917 54.89 2.49917 54.38 2.80917L48.86 15.9892C53.43 15.8492 57.61 17.5092 61.04 20.4892M19.8 34.5292C22.45 35.5292 26.15 38.6792 28.81 40.2792C39.51 46.7392 62.72 58.4792 66.13 38.3892C67.79 28.6092 59.75 16.8692 49.21 17.4692C48.73 17.4992 48.39 17.4292 48.03 17.8492L39.85 36.2592L40.39 37.3492C40.19 37.5992 37.94 37.5692 37.5 37.5292C36.23 37.4192 37.58 36.7392 37.93 36.1992C38.42 35.4592 38.77 34.6592 39.18 33.8792C44.37 23.7592 48.42 12.9892 52.9 2.52917L52.52 0.299167L60.75 0.209167C60.73 0.959167 59.82 1.14917 60.28 2.07917C62.48 9.95917 66.05 17.7692 68.22 25.6092C71.07 35.8792 68.45 46.1892 58.34 51.0992C45.8 57.1892 31.05 49.9092 21.68 41.2292C18.5 38.2892 15.95 34.4892 11.35 33.5492C11.38 32.9692 11.52 33.1892 11.79 33.0992C19.28 30.7192 25.81 27.4392 28.53 19.3092C30 14.9192 30.29 5.21917 25.09 3.12917C19.65 0.959167 13.29 1.59917 7.59 1.26917L7.26 1.93917V48.1892L8.39 49.4392C8.33 49.9192 8.33 49.8392 7.96 49.8892C5.58 50.1992 2.69 49.5092 0.27 50.0492C-0.32 49.3492 1.28 48.9692 1.33 48.2292C0.91 34.0992 1.54 19.9292 1.23 5.80917C1.21 4.85917 1.3 2.82917 1.15 2.05917C1.02 1.36917 0.08 0.969167 0 0.229167C0.33 0.319167 0.67 0.429167 1.02 0.439167C8.16 0.559167 20.79 -0.880833 27.22 0.889167C37.44 3.69917 35.74 17.2592 30.59 23.8892C26.32 29.3892 22.05 30.0392 16.49 32.9392C16.2 33.0892 15.93 33.0192 15.92 33.4992C17.15 33.9192 18.61 34.0792 19.81 34.5292"/>
        <path d="M112.13 45.8392C112.03 46.0792 111.33 47.2492 111.13 47.3992C110.77 47.6792 107.84 48.5392 107.2 48.6992C93.47 52.0292 77.03 46.6092 73.84 31.5192C70.22 14.4192 80.99 -1.86083 99.41 0.449167C102.61 0.849167 106.58 2.49917 109.63 2.59917C110.36 2.61917 110.85 2.10917 111.54 2.10917C112 2.66917 111.51 3.29917 111.5 3.92917C111.45 5.52917 111.54 7.14917 111.45 8.74917C111.44 8.92917 111.65 9.39917 111.14 9.31917C110.99 9.29917 109.71 7.15917 109.36 6.76917C104.42 1.28917 93.86 0.389166 87.81 4.49917C78.92 10.5292 77.52 27.6692 82.11 36.6992C86.98 46.2892 99.83 52.1992 109.24 45.3192L113.36 41.2492C112.85 42.6892 112.68 44.4492 112.13 45.8492"/>
      </g>
      <g class="page-loader__heal">
        <path d="M163.13 0.459167L162.32 1.64917C161.79 16.7892 162.4 32.0392 162.1 47.2292C162.12 47.7592 162.23 48.3392 162.48 48.7992C162.81 49.3992 163.37 49.5592 163.54 50.0792L155.02 49.8292L156.2 48.0692C155.73 43.3092 156.58 38.0092 156.15 33.3192C156.03 31.9792 154.69 30.9692 153.66 30.1792C147.32 25.3192 138.32 24.6492 130.61 24.8992L130.52 48.1592L131.68 50.1692C128.98 49.6992 126.14 50.1092 123.4 49.9792C124.14 49.1292 124.51 48.3692 124.59 47.1992L124.61 25.5492C116.91 26.2892 108.3 28.5892 100.69 25.7392C97.75 24.6392 96.31 22.1092 95.19 19.3492L98.85 22.4092C102.69 24.3692 106.95 23.8592 111.08 23.3692C115.57 22.8392 119.98 21.6692 124.48 21.1992L124.56 2.54917C124.53 2.22917 124.39 1.81917 124.21 1.55917C123.96 1.19917 123.12 0.969167 123.43 0.619167C123.88 0.109167 130.29 0.829167 131.45 0.349167C132.42 0.669167 130.87 1.07917 130.75 1.56917C130.08 7.93917 130.77 14.3992 130.42 20.7992C135.25 21.1392 140.34 21.4392 144.67 23.8192C148.93 25.2292 152.48 28.0992 156.07 30.6792L155.98 1.71917L154.73 0.639167L163.11 0.479167L163.13 0.459167Z"/>
        <path d="M197.59 0.339167L198.82 4.77917C197.57 3.72917 195.34 1.81917 193.65 1.81917H174.67V17.5992H189.71C190.85 17.5992 193.27 14.9592 193.4 15.1292V21.5392C192.5 20.8892 190.24 19.0692 189.21 19.0692H174.67V47.9092L175.41 48.6492H196.12C196.64 48.6492 200.21 47.7192 200.77 47.3792C201.76 46.7892 201.93 46.0092 202.53 45.1892L201.49 50.1492L167.77 49.6192L168.78 47.4392C167.66 33.2192 170.16 16.9792 168.78 3.00917C168.66 1.75917 168.56 1.33917 167.76 0.329167H197.59V0.339167Z"/>
        <path d="M232.57 29.4492L223.45 3.30918L215.81 24.0192C221.95 23.9992 227.71 25.6692 232.58 29.4492M238.49 49.6792C238.98 48.7392 239.04 48.1492 238.89 47.0992C238.6 45.0192 235 34.9892 233.82 33.3892C229.78 27.8892 221.66 25.6892 215.11 25.9692L207.49 47.5992L208.39 50.1692L203.95 49.6792C204.44 48.8592 205.02 48.4292 205.46 47.4892C210.24 37.2792 215.13 21.0592 218.76 9.97918C219.79 6.82918 221.42 3.64918 220.97 0.349182H230.59L230.31 3.32918C235.97 16.9792 239.06 33.5492 245.01 46.8592C245.55 48.0592 246.14 48.6692 246.87 49.6792H238.48H238.49Z"/>
        <path d="M259.71 0.339167C259.71 0.949167 258.23 1.80917 258.23 2.06917V48.1692H279.18C280.02 48.1692 282.24 45.6192 282.63 44.7092L281.59 49.6792L251.33 50.1392C250.96 49.2592 252.31 48.6292 252.31 48.4192V1.32917H251.33V0.339167H259.71Z"/>
      </g>
    `;
  }

  private beginTrackingMedia(): void {
    const media = Array.from(document.querySelectorAll(this.trackSelector)).filter((element) => {
      if (!element || !('tagName' in element)) {
        return false;
      }

      if (!this.overlayElement) {
        return true;
      }

      return !this.overlayElement.contains(element as Node);
    }) as Array<HTMLImageElement | HTMLVideoElement>;

    if (!media.length) {
      this.log('No trackable media found; finishing immediately');
      this.finishTracking('no-media');
      return;
    }

    media.forEach((element, index) => {
      const id = `${element.tagName.toLowerCase()}-${index}`;
      this.itemProgress.set(id, 0);
      const cleanup = this.trackMediaElement(id, element);
      this.trackedMedia.push({ id, element, cleanup });
    });

    this.log('Tracking media started', {
      trackedItems: media.length,
      selector: this.trackSelector,
      startedAt: new Date().toISOString(),
    });

    this.updateOverallProgress();
  }

  private trackMediaElement(id: string, element: HTMLImageElement | HTMLVideoElement): () => void {
    if (element.tagName.toLowerCase() === 'img') {
      return this.trackImage(id, element as HTMLImageElement);
    }

    return this.trackVideo(id, element as HTMLVideoElement);
  }

  private trackImage(id: string, image: HTMLImageElement): () => void {
    if (image.complete) {
      this.updateItemProgress(id, 100);
      return () => {};
    }

    const onLoad = (): void => this.updateItemProgress(id, 100);
    const onError = (): void => this.updateItemProgress(id, 100);

    image.addEventListener('load', onLoad);
    image.addEventListener('error', onError);

    const timeout = window.setTimeout(() => {
      this.log('Image timeout', image.currentSrc || image.src);
      this.updateItemProgress(id, 100);
    }, this.elementTimeoutMs);

    return (): void => {
      image.removeEventListener('load', onLoad);
      image.removeEventListener('error', onError);
      window.clearTimeout(timeout);
    };
  }

  private trackVideo(id: string, video: HTMLVideoElement): () => void {
    if (video.readyState >= 3) {
      this.updateItemProgress(id, 100);
      return () => {};
    }

    const onLoadedMetadata = (): void => this.updateItemProgress(id, 25);
    const onCanPlay = (): void => this.updateItemProgress(id, 75);
    const onCanPlayThrough = (): void => this.updateItemProgress(id, 100);
    const onSuspend = (): void => this.updateItemProgress(id, 100);
    const onError = (): void => this.updateItemProgress(id, 100);
    const onProgress = (): void => {
      if (!video.duration || !isFinite(video.duration) || video.buffered.length < 1) {
        return;
      }

      const loadedSeconds = video.buffered.end(video.buffered.length - 1);
      const fraction = Math.min(loadedSeconds / video.duration, 1);
      this.updateItemProgress(id, Math.round(fraction * 100));
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('canplaythrough', onCanPlayThrough);
    video.addEventListener('suspend', onSuspend);
    video.addEventListener('error', onError);
    video.addEventListener('progress', onProgress);

    const timeout = window.setTimeout(() => {
      this.log('Video timeout', video.currentSrc || video.src);
      this.updateItemProgress(id, 100);
    }, this.elementTimeoutMs);

    return (): void => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('canplaythrough', onCanPlayThrough);
      video.removeEventListener('suspend', onSuspend);
      video.removeEventListener('error', onError);
      video.removeEventListener('progress', onProgress);
      window.clearTimeout(timeout);
    };
  }

  private updateItemProgress(id: string, value: number): void {
    const current = this.itemProgress.get(id) || 0;
    const normalized = Math.min(Math.max(value, 0), 100);

    if (normalized <= current) {
      return;
    }

    this.itemProgress.set(id, normalized);
    this.updateOverallProgress();
  }

  private updateOverallProgress(): void {
    if (!this.itemProgress.size) {
      this.targetProgress = 1;
      return;
    }

    const total = Array.from(this.itemProgress.values()).reduce((sum, value) => sum + value, 0);
    this.targetProgress = Math.min(total / (this.itemProgress.size * 100), 1);

    if (this.targetProgress >= 1) {
      this.finishTracking('all-media-complete');
    }
  }

  private finishTracking(reason: string = 'unknown'): void {
    this.targetProgress = 1;
    this.log('finishTracking invoked', {
      reason,
      targetProgress: this.targetProgress,
      displayProgress: this.displayProgress,
      trackedItems: this.itemProgress.size,
      elapsedMs: Date.now() - this.startTime,
    });

    if (this.completionStarted) {
      this.log('Completion already started; finishTracking ignored');
      return;
    }

    const elapsed = Date.now() - this.startTime;
    const waitForMinimum = Math.max(this.minimumDurationMs - elapsed, 0);
    this.log('Completion gate scheduled', {
      waitForMinimumMs: waitForMinimum,
      elapsedMs: elapsed,
      minimumDurationMs: this.minimumDurationMs,
    });

    window.setTimeout(() => {
      if (!this.completionStarted && this.hasMetCompletionThreshold()) {
        this.log('Completion gate passed; starting completion sequence', {
          displayProgress: this.displayProgress,
          targetProgress: this.targetProgress,
          threshold: this.completionDisplayThreshold,
        });
        this.queueCompletionSequence();
      } else {
        this.log('Completion gate blocked', {
          completionStarted: this.completionStarted,
          displayProgress: this.displayProgress,
          targetProgress: this.targetProgress,
          threshold: this.completionDisplayThreshold,
        });

        if (!this.completionStarted) {
          window.setTimeout(() => {
            this.finishTracking('completion-gate-retry');
          }, 120);
        }
      }
    }, waitForMinimum + 50);
  }

  private hasMetCompletionThreshold(): boolean {
    if (this.completionDisplayThreshold === null) {
      return true;
    }

    return this.displayProgress >= this.completionDisplayThreshold;
  }

  private startProgressLoop(): void {
    const tick = (): void => {
      const smoothing = this.completionQueued ? Math.max(this.progressSmoothing, 0.45) : this.progressSmoothing;
      this.displayProgress += (this.targetProgress - this.displayProgress) * smoothing;

      if (this.targetProgress >= 1 && this.displayProgress > 0.995) {
        this.displayProgress = 1;
      }

      this.applyProgress(this.displayProgress);

      if (this.completionQueued && !this.completionStarted && this.displayProgress >= 1) {
        this.log('Catch-up fill complete; starting completion sequence');
        this.startCompletionSequence();
      }

      if (!this.completionStarted || this.displayProgress < 1) {
        this.rafId = requestAnimationFrame(tick);
      }
    };

    this.rafId = requestAnimationFrame(tick);
  }

  private applyProgress(value: number): void {
    if (!this.overlayElement) {
      return;
    }

    const progress = Math.min(Math.max(value, 0), 1);
    this.overlayElement.style.setProperty('--loader-progress', progress.toFixed(4));
  }

  private queueCompletionSequence(): void {
    if (this.completionStarted || this.completionQueued) {
      return;
    }

    this.completionQueued = true;
    this.targetProgress = 1;
    this.log('Completion queued; fast catch-up to full fill started', {
      displayProgress: this.displayProgress,
      targetProgress: this.targetProgress,
    });
  }

  private startCompletionSequence(): void {
    if (!this.overlayElement || this.completionStarted) {
      this.log('startCompletionSequence skipped', {
        hasOverlay: Boolean(this.overlayElement),
        completionStarted: this.completionStarted,
      });
      return;
    }

    this.completionStarted = true;
  this.completionQueued = false;
    this.log('Completion sequence started');

    this.overlayElement.classList.add('is-heal');

    window.setTimeout(() => {
      if (!this.overlayElement) {
        this.log('Overlay missing before exit animation');
        return;
      }

      this.overlayElement.classList.add('is-exit');
      this.log('Exit animation started');

      window.setTimeout(() => {
        this.log('Loader teardown complete');
        this.markShown();
        this.destroy();
      }, 460);
    }, 440);
  }
}

export default PageLoader;
